import { ReactNode, useEffect, useRef } from 'react'
import FileViewerContext from './fileViewerContext'
import FileViewerContainer, { FileViewerRef } from './FileViewerContainer'
import { Box, Stack, useMediaQuery, useTheme } from '@mui/material'

type FileViewerProviderProps = {
  onUndock: () => void
  onDock: () => void
  onClose: () => void
  children: ReactNode
}

/**
 * A provider component that wraps the entire app and provides the context
 * for the useFileViewer() hook to work correctly. This also manages the
 * layout of the viewer component in relation to the rest of the page
 */
export default function FileViewerProvider({
  onUndock,
  onDock,
  onClose,
  children,
}: Readonly<FileViewerProviderProps>) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // A shared ref for use in the file viewer component. This allows state to be
  // passed between the hook and components without causing re-renders of the
  // entire app (as this provider wraps the whole app).
  //
  // NOTE: don't add any props/state in this component!
  const fileViewerRef = useRef<FileViewerRef | null>(null)

  useEffect(() => {
    // listen for navigation events starting in either the React app, or the
    // AngularJS app, and close the file viewer if it is open
    const navigationListener = () => {
      fileViewerRef.current?.setIsFileVisible(false)
    }
    window.addEventListener('@@loaded/reactNavigation', navigationListener)
    window.addEventListener('@@loaded/angularNavigation', navigationListener)

    return () => {
      window.removeEventListener('@@loaded/reactNavigation', navigationListener)
      window.removeEventListener(
        '@@loaded/angularNavigation',
        navigationListener
      )
    }
  })

  return (
    <FileViewerContext.Provider
      value={{
        fileViewerRef,
      }}
    >
      <Stack direction={'row'} useFlexGap={true} gap={'0px'}>
        <Box
          flexGrow={0} // Do not grow when no content inside this box
          flexBasis={'auto'} // set the width to the size of the content
          maxWidth={'33%'} // but restrict to 33% of the parent container
        >
          <FileViewerContainer
            ref={fileViewerRef}
            isMobile={isMobile}
            onUndock={onUndock}
            onDock={onDock}
            onClose={onClose}
          />
        </Box>
        <Box
          flexGrow={1} // Grow to fill the remaining space - i.e. 100% width if no file viewer content
          overflow={'auto'}
        >
          {children}
        </Box>
      </Stack>
    </FileViewerContext.Provider>
  )
}
